import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Loader2, Radio, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Copy } from "lucide-react";

/** BroadcastPanel — admin-triggered ad-hoc SMS to a filtered audience.
 * Live TCPA guard rails: consent + valid phone required per recipient.
 * Two-step UX: preview shows the audience count + segments, then Send fires. */
export default function BroadcastPanel() {
  const [expanded, setExpanded] = useState(false);
  const [body, setBody] = useState(
    "Thank you for attending. Don't forget to upload your photos and videos on Kalulu. We can't wait to see the moment you captured. Click here to upload pitch.businessshowers.org/event/photos"
  );
  const [audience, setAudience] = useState("checked_in");
  const [eventDay, setEventDay] = useState("2026-07-17");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const runPreview = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/event/admin/broadcast/preview", {
        body, audience, event_day: eventDay,
      });
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const runSend = async () => {
    if (!preview) { toast.error("Run preview first"); return; }
    if (!window.confirm(
      `Send this SMS to ${preview.reachable_count} recipient(s)?\n\n` +
      `"${body.slice(0, 100)}${body.length > 100 ? "…" : ""}"\n\n` +
      `Segments: ${preview.segments_per_message} · Skipped: ${preview.skipped_no_consent_or_phone}\n\n` +
      `This cannot be undone.`
    )) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/event/admin/broadcast/send", {
        body, audience, event_day: eventDay,
      });
      setResult(data);
      if (data.sent > 0) toast.success(`Broadcast sent to ${data.sent} recipient${data.sent === 1 ? "" : "s"}`);
      else if (data.failed > 0) toast.error(`All ${data.failed} sends failed. Check the SMS log below.`);
      else toast.warning(`No sends fired. Audience: ${data.audience_total}, skipped: ${data.skipped}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Broadcast failed");
    } finally { setBusy(false); }
  };

  const length = body.length;
  const segments = length <= 160 ? 1 : Math.ceil((length - 160) / 153) + 1;

  return (
    <Card data-testid="broadcast-panel">
      <CardHeader>
        <button
          onClick={() => setExpanded((v) => !v)}
          data-testid="broadcast-toggle"
          className="w-full flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-2">
            <Radio size={16} className="text-fuchsia-700" />
            <h3 className="font-[Cormorant_Garamond] text-xl">Broadcast SMS</h3>
            <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold bg-fuchsia-100 text-fuchsia-800 px-2 py-0.5 rounded-full">New</span>
          </div>
          {expanded ? <ChevronUp size={16} className="text-emerald-700" /> : <ChevronDown size={16} className="text-emerald-700" />}
        </button>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-4">
          <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-3 text-xs font-[Outfit] text-amber-900">
            <div className="font-bold flex items-center gap-1.5"><AlertTriangle size={14} /> Live send · read before firing</div>
            <ul className="mt-1 space-y-0.5 list-disc ml-5">
              <li>Only recipients who opted in to SMS + have a valid phone number are contacted.</li>
              <li>Test Mode routes messages to the configured test recipient (never real attendees).</li>
              <li>Every send is logged to Recent Activity below.</li>
            </ul>
          </div>

          {/* Message body */}
          <div>
            <div className="flex justify-between items-baseline mb-1">
              <label className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Message</label>
              <span className={`text-[10px] font-[Outfit] font-semibold ${length > 480 ? "text-red-600" : segments > 1 ? "text-amber-600" : "text-emerald-700"}`}>
                {length} chars · {segments} SMS segment{segments === 1 ? "" : "s"}
              </span>
            </div>
            <textarea
              data-testid="broadcast-body"
              value={body}
              onChange={(e) => { setBody(e.target.value); setPreview(null); setResult(null); }}
              maxLength={500}
              rows={4}
              className="w-full rounded-xl border-2 border-emerald-200 bg-white px-3 py-2 text-sm font-[Outfit] focus:border-fuchsia-500 outline-none"
            />
            <div className="text-[10px] mt-1 text-emerald-800/50 font-[Outfit]">
              TCPA note: recipients can always reply STOP to opt out.
            </div>
          </div>

          {/* Audience picker */}
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60 col-span-2">Audience</label>
            <select
              data-testid="broadcast-audience"
              value={audience}
              onChange={(e) => { setAudience(e.target.value); setPreview(null); }}
              className="h-10 rounded-lg border-2 border-emerald-200 bg-white px-2 text-sm font-[Outfit]"
            >
              <option value="checked_in">Checked in only</option>
              <option value="all_registered">All registered</option>
            </select>
            <select
              data-testid="broadcast-event-day"
              value={eventDay}
              onChange={(e) => { setEventDay(e.target.value); setPreview(null); }}
              className="h-10 rounded-lg border-2 border-emerald-200 bg-white px-2 text-sm font-[Outfit]"
            >
              <option value="2026-07-17">Friday · Jul 17</option>
              <option value="2026-07-18">Saturday · Jul 18</option>
              <option value="both">Both nights</option>
            </select>
          </div>

          {/* Preview */}
          {preview && (
            <div data-testid="broadcast-preview" className="rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-3 text-sm font-[Outfit] text-emerald-950">
              <div className="font-bold mb-1">Audience preview · {preview.filter_summary}</div>
              <ul className="text-xs space-y-0.5">
                <li>Attendees matched: <b>{preview.audience_total}</b></li>
                <li>Reachable (consented + phone): <b className="text-emerald-700">{preview.reachable_count}</b></li>
                <li>Will be skipped (no consent or phone): {preview.skipped_no_consent_or_phone}</li>
                <li>Message: {preview.body_length} chars · {preview.segments_per_message} SMS segment(s)</li>
              </ul>
              {preview.sample_recipients?.length > 0 && (
                <div className="mt-2 text-[11px] text-emerald-800">
                  Sample: {preview.sample_recipients.map((r, i) => <span key={i}>{r.name} (···{r.phone_last4}){i < preview.sample_recipients.length - 1 ? ", " : ""}</span>)}
                </div>
              )}
            </div>
          )}

          {/* Result */}
          {result && (
            <div data-testid="broadcast-result" className={`rounded-xl border-2 p-3 text-sm font-[Outfit] ${result.failed > 0 ? "border-red-200 bg-red-50 text-red-900" : "border-emerald-300 bg-emerald-50 text-emerald-900"}`}>
              <div className="font-bold flex items-center gap-1"><CheckCircle2 size={14} /> Broadcast complete</div>
              <div className="mt-1 text-xs">
                Sent: <b>{result.sent}</b> · Failed: <b>{result.failed}</b> · Skipped: <b>{result.skipped}</b>
              </div>
              {result.errors?.length > 0 && (
                <div className="mt-1 text-[11px]">
                  Sample errors: {result.errors.slice(0, 2).join(" · ")}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <Button
              onClick={runPreview}
              disabled={busy || !body.trim()}
              data-testid="broadcast-preview-btn"
              variant="outline"
              className="rounded-full h-10 flex-1"
            >
              {busy && !preview ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
              Preview audience
            </Button>
            <Button
              onClick={runSend}
              disabled={busy || !preview || preview.reachable_count === 0}
              data-testid="broadcast-send-btn"
              className="rounded-full h-10 flex-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-bold"
            >
              {busy && preview ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Radio size={14} className="mr-1.5" />}
              Send to {preview?.reachable_count ?? "?"} recipient{preview?.reachable_count === 1 ? "" : "s"}
            </Button>
            <Button
              onClick={() => { navigator.clipboard.writeText(body); toast.success("Copied to clipboard"); }}
              variant="ghost"
              size="icon"
              className="rounded-full h-10 w-10"
              title="Copy message"
            >
              <Copy size={14} />
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
